import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TableFooter,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from "@mui/material";
import MargemLineChart from "./MargemLineChart.component";
import { useGetOrdersMetrics } from "../../../client/hooks/orders/order.queries";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { LineChart } from "@mui/x-charts";

export default function OrderMargemSection({ filters, refsetch }) {
  const [canFetch, setCanFetch] = useState(false);
  const { data, isRefetching, refetch } = useGetOrdersMetrics(
    canFetch,
    filters,
  );

  useEffect(() => {
    setCanFetch(data ? false : true);
  }, [data]);

  useEffect(() => {
    if (!isRefetching) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refsetch]);

  // Ordenar os dados de marketplace com base no total (em ordem decrescente)
  const sortedMarketplaces =
    data?.marketplaces_margem?.sort((a, b) => b.total - a.total) || [];

  // Calcular as somas de VENDA(+), =MAR e MAR%
  const totalVenda = sortedMarketplaces.reduce(
    (sum, row) => sum + (row.total || 0),
    0,
  );
  const totalEqualMar = sortedMarketplaces.reduce(
    (sum, row) => sum + (row.equalMar || 0),
    0,
  );
  const totalMarPercentage = (totalEqualMar * 100) / totalVenda || 0;

  const [visibleLines, setVisibleLines] = React.useState({
    faturamento: true,
    ticketMedio: true,
    quantidade: true,
  });

  // Função para alternar a visibilidade de uma linha
  const toggleLineVisibility = (line) => {
    setVisibleLines((prevState) => ({
      ...prevState,
      [line]: !prevState[line],
    }));
  };

  // Extração de dados para os gráficos
  const series = [
    {
      label: "Faturamento R$",
      showMark: false,
      data: data?.pedidos?.map((item) => item.total) || [],
      color: "#FF6347", // Cor para Faturamento
      area: true,
      visible: visibleLines.faturamento,
    },
    {
      label: "Ticket Médio R$",
      showMark: false,
      data: data?.pedidos?.map((item) => item.ticketMedio) || [],
      color: "#4CAF50", // Cor para Ticket Médio
      area: true,
      visible: visibleLines.ticketMedio,
    },
    {
      label: "Quantidade Pedidos",
      showMark: false,
      data: data?.pedidos?.map((item) => item.quantidade) || [],
      color: "#1E90FF", // Cor para Quantidade Pedidos
      area: true,
      visible: visibleLines.quantidade,
    },
  ];

  const xLabels =
    data?.pedidos?.map((item) =>
      moment(item.date, "YYYY-MM-DD").format("DD/MM"),
    ) || [];

  return (
    <Box
      sx={{
        width: "auto",
        gap: 6,
        margin: "20px 0 20px 0",
        padding: "10px",
        justifyContent: "center",
      }}
    >
      {/* Linha de Cards e Gráficos */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "auto",
          rowGap: "15px",
        }}
      >
        {/* Primeira linha: Cards */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            width: "100%",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          {/* Margem Total */}
          <Card
            sx={{
              flex: "1 1 calc(33.33% - 16px)",
              minWidth: 240,
              borderRadius: 2,
              boxShadow: 2,
              backgroundColor: "white",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 3,
                backgroundColor: "#90caf9", // azul claro
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
              }}
            />
            <CardContent>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                gutterBottom
              >
                Margem Total
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1976d2" }}
              >
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(data?.totalMargem?.toFixed(2) || 0)}
              </Typography>
            </CardContent>
          </Card>

          {/* Margem Negativa */}
          <Card
            sx={{
              flex: "1 1 calc(33.33% - 16px)",
              minWidth: 240,
              borderRadius: 2,
              boxShadow: 2,
              backgroundColor: "white",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 3,
                backgroundColor: "#ef9a9a", // vermelho claro
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
              }}
            />
            <CardContent>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                gutterBottom
              >
                Margem Negativa
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#c62828" }}
              >
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(data?.margemNegativa?.toFixed(2) || 0)}
              </Typography>
            </CardContent>
          </Card>

          {/* Margem Positiva */}
          <Card
            sx={{
              flex: "1 1 calc(33.33% - 16px)",
              minWidth: 240,
              borderRadius: 2,
              boxShadow: 2,
              backgroundColor: "white",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 3,
                backgroundColor: "#a5d6a7", // verde claro
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
              }}
            />
            <CardContent>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                gutterBottom
              >
                Margem Positiva
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#2e7d32" }}
              >
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(data?.margemPositiva?.toFixed(2) || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Segunda linha: Gráficos */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center", // Centraliza verticalmente
            height: "100%", // Certifique-se de que o pai tenha altura definida
            width: "100%",
          }}
        >
          {/* Tabela */}
          <Box display={"flex"} sx={{ width: "100%", height: "650px" }}>
            {" "}
            {/* Set fixed height */}
            <TableContainer
              sx={{
                boxShadow: "2px 2px 10px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
                maxWidth: "100%",
                minWidth: "550px",
                height: "100%", // Fill parent height
                display: "flex", // Use flex to ensure proper layout
              }}
              component={Paper}
            >
              <Table
                size="small"
                aria-label="tabela"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "hidden", // Prevent outer overflow
                }}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#34495e",
                    width: "100%",
                    display: "flex",
                  }}
                >
                  <TableRow
                    sx={{ display: "flex", width: "100%", height: "100%" }}
                  >
                    <TableCell
                      align="left"
                      title="MARKETPLACE"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "14px",
                        padding: "10px",
                        width: "40%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      MARKETPLACE
                    </TableCell>
                    <TableCell
                      align="right"
                      title="VENDA(+)"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "14px",
                        padding: "10px",
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      VENDA(+)
                    </TableCell>
                    <TableCell
                      align="right"
                      title="=MAR"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "14px",
                        padding: "10px",
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      =MAR
                    </TableCell>
                    <TableCell
                      align="right"
                      title="MAR%"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "14px",
                        padding: "10px",
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      MAR%
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    overflowY: "auto", // Enable vertical scrolling
                    flex: 1, // Take available space
                    width: "100%",
                    display: "block", // This allows scrolling
                  }}
                >
                  {sortedMarketplaces.map((row, index) => {
                    const total = new Intl.NumberFormat("pt-Br", {
                      style: "currency",
                      currency: "BRL",
                    }).format(row?.total?.toFixed(2) || 0);
                    const equalMar = new Intl.NumberFormat("pt-Br", {
                      style: "currency",
                      currency: "BRL",
                    }).format(row?.equalMar?.toFixed(2) || 0);
                    const marPercentage = Number(
                      ((row?.equalMar * 100) / row?.total).toFixed(2) || 0,
                    );
                    return (
                      <TableRow
                        key={row.name}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#ecf0f1" : "#fff",
                          "&:hover": {
                            backgroundColor: "#dfe6e9",
                          },
                          transition: "background-color 0.2s",
                          display: "flex",
                          width: "100%",
                        }}
                      >
                        <TableCell
                          align="left"
                          title={row.name}
                          sx={{
                            fontSize: "13px",
                            color: "#2c3e50",
                            padding: "8px",
                            width: "40%", // Increased width for marketplace column
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          title={total}
                          sx={{
                            fontSize: "13px",
                            color: "#1a73c3",
                            padding: "8px",
                            width: "20%", // Adjusted width
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {total}
                        </TableCell>
                        <TableCell
                          align="right"
                          title={equalMar}
                          sx={{
                            fontSize: "13px",
                            color: "#2a8323",
                            padding: "8px",
                            width: "20%", // Adjusted width
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {equalMar}
                        </TableCell>
                        <TableCell
                          align="right"
                          title={`${marPercentage}%`}
                          sx={{
                            fontSize: "13px",
                            color: "#2c3e50",
                            padding: "8px",
                            backgroundColor:
                              marPercentage > 7 ? "#deefff" : "#f0e199",
                            width: "20%", // Adjusted width
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {marPercentage}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                <TableFooter
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    backgroundColor: "#34495e",
                    width: "100%",
                    display: "flex", // Match header display
                  }}
                >
                  <TableRow
                    sx={{ display: "flex", width: "100%", height: "100%" }}
                  >
                    <TableCell
                      align="left"
                      title="Totais"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold", // Same weight as header
                        fontSize: "14px", // Same size as header
                        padding: "10px", // Exactly matching header padding
                        width: "40%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Totais
                    </TableCell>
                    <TableCell
                      align="right"
                      title={new Intl.NumberFormat("pt-Br", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalVenda.toFixed(2))}
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "14px", // Match header font size
                        padding: "10px", // Match header padding exactly
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {new Intl.NumberFormat("pt-Br", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalVenda.toFixed(2))}
                    </TableCell>
                    <TableCell
                      align="right"
                      title={new Intl.NumberFormat("pt-Br", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalEqualMar.toFixed(2))}
                      sx={{
                        color: "#fff",
                        // fontWeight: "bold",
                        fontSize: "14px", // Match header font size
                        padding: "10px", // Match header padding exactly
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {new Intl.NumberFormat("pt-Br", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalEqualMar.toFixed(2))}
                    </TableCell>
                    <TableCell
                      align="right"
                      title={`${new Intl.NumberFormat("pt-Br", { style: "decimal" }).format(totalMarPercentage.toFixed(2))}%`}
                      sx={{
                        color: "#fff",
                        // fontWeight: "bold",
                        fontSize: "14px", // Match header font size
                        padding: "10px", // Match header padding exactly
                        width: "20%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {new Intl.NumberFormat("pt-Br", {
                        style: "decimal",
                      }).format(totalMarPercentage.toFixed(2))}
                      %
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
          <Box
            sx={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              ml: 5,
            }}
          >
            <Box
              sx={{
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                ml: 5,
              }}
            >
              {/* Controle de visibilidade das linhas */}
              <Box sx={{ marginBottom: "10px" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleLines.faturamento}
                      onChange={() => toggleLineVisibility("faturamento")}
                      sx={{
                        color: "#ff7655",
                        "&.Mui-checked": {
                          color: "#ff7655",
                        },
                      }}
                    />
                  }
                  label="Faturamento R$"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleLines.ticketMedio}
                      onChange={() => toggleLineVisibility("ticketMedio")}
                      sx={{
                        color: "#5bd160",
                        "&.Mui-checked": {
                          color: "#5bd160",
                        },
                      }}
                    />
                  }
                  label="Ticket Médio R$"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleLines.quantidade}
                      onChange={() => toggleLineVisibility("quantidade")}
                      sx={{
                        color: "#24acff",
                        "&.Mui-checked": {
                          color: "#24acff",
                        },
                      }}
                    />
                  }
                  label="Quantidade Pedidos"
                />
              </Box>

              {/* Gráfico de Linhas */}
              <LineChart
                width={800}
                height={280}
                series={series.filter((line) => line.visible)} // Exibe apenas as linhas visíveis
                yAxis={[{ disableTicks: true, disableLine: false }]}
                grid={{ horizontal: true, vertical: false }}
                xAxis={[
                  {
                    scaleType: "point",
                    data: xLabels,
                    disableTicks: false,
                    disableLine: true,
                  },
                ]}
                slotProps={{ legend: { hidden: true } }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center", // Centraliza verticalmente
                justifyContent: "center",
                height: "100%", // Certifique-se de que o pai tenha altura definida
                width: "100%",
                mb: 3,
              }}
            >
              <MargemLineChart data={data} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
